import {
  EXCLUDE_END_DATE_CATEGORIES,
  EXCLUDE_CONTRACT_RECURRING_MODE
} from '@nile/banyan/src/Pages/ServiceCatalog/constants';
import { EndDate } from '@nile/banyan/src/Pebbles/ServiceCatalog/catalogForm';
import {
  AppContext,
  DATE_TIME_FORMAT,
  DropdownIndicator,
  formatGroupLabel,
  getTranslatedText,
  IndicatorSeparator,
  SuperPop,
  useCurrencySymbols
} from '@nile/components';
import {
  Checkbox,
  Dropdown,
  Label,
  TextField,
  Heading,
  Button,
  DateTimePicker as DatePicker,
  Badge,
  Spinner
} from '@superops/ui-design-system';
import {
  GET_PRORATED_AMOUNT,
  GET_NEXT_BILL_DATE,
  AccountClient,
  TicketClient
} from '@nile/graphql';
import { useLazyQuery } from '@apollo/client';
import dompurify from 'dompurify';
import { Tooltip } from 'react-clone-tippy';
import { SVGIcon } from '@superops/super-icons';
import dayjs from 'dayjs';
import { includes, isEmpty, isEqual, pick } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { withLDConsumer, useFlags } from 'launchdarkly-react-client-sdk';
// import DatePicker from 'react-datepicker';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { ServiceItemSheetContext } from '@nile/client_management/src/Pages/ContractTemplate/NewServiceItemSheet';

import ItemQuantity from './ItemQuantity';
import QuantityCollector from './QuantityCollector';
import SubscriptionSelectField from './SubscriptionSelectField';
import ItemQuantitySubType from './ItemQuantitySubType';
import getMinEndDate from './helpers';

const Field = styled.div`
  margin-top: 24px;
  width: 200px;
  .item-quantity {
    padding-left: 0 !important;
  }
  .react-datepicker__input-container > input {
    height: 38px;
    width: 200px;
  }
  .Superops-Dropdown-container {
    min-width: 200px;
  }
`;

const ProrateWrap = styled.div`
  margin-top: 24px;
  .item-quantity {
    padding-left: 0 !important;
  }
  .react-datepicker__input-container > input {
    height: 38px;
    width: 200px;
  }
`;
export const UnitType = styled.div`
  padding: 0px 12px;
  border-radius: 8px;
  height: 100%;
  line-height: 2.8rem;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  border-bottom: 1px solid #ececec;
  color: rgba(0, 0, 0, 0.8);
  background: rgba(196, 196, 196, 0.3);
`;

const Operation = styled.div`
  background: ${(props) =>
    props.selected
      ? props.type === 'DECREASE'
        ? '#EC5151'
        : '#77BB61'
      : 'transprent'};
  transform: ${(props) =>
    props.type === 'DECREASE' ? 'rotate(0deg)' : 'rotate(180deg)'};
`;

/**
 * Section to be rendered for getting unit type item configuration
 * @public
 */

function UnitTypeSection(props) {
  const { company } = useContext(AppContext);
  const currencyCode = company?.currency || 'USD';
  const countryCode = company?.countryCode || 'US';
  const {
    intl,
    serviceItem,
    actions,
    errors,
    handlePropChange,
    handlePropertiesChange,
    subscriptionTypes,
    customSubscriptionTypes,
    configurableSections,
    disabledSections,
    isDisabled,
    isEditEndDatedisabled,
    totalAssets,
    totalUsers,
    clientId,
    formType,
    enableReminder,
    integrationAddOn,
    isIntegrationAddOnItem,
    entryPoint,
    subscriptionItemQuantityCount,
    isSiteSpecificEnabled,
    refetchSubscriptionQuantity,
    handleAssetTypeChange,
    nextBillDate,
    nextBillDateLoading
  } = props;
  const {
    name,
    canEdit,
    invoiceDescription,
    category,
    quantity,
    quantityType,
    roundUpValue,
    unitPrice,
    costPrice,
    // subscriptionType,
    frequency,
    useAsWorklogItem,
    businessHoursUnitPrice,
    afterHoursUnitPrice,
    effectiveDate,
    billDate,
    endDate,
    carryForwardRemainingBlockHours,
    unitType,
    changes,
    change,
    unitPriceOverridden,
    remindDuration
  } = serviceItem;

  const [initialServiceItem] = useState(serviceItem);

  const subscriptionType = frequency?.type;

  const { currencySymbolWithCode } = useCurrencySymbols();

  const serviceItemSheetCtx = useContext(ServiceItemSheetContext);

  let showChanges = true;

  const changeHasEffectiveDate = changes?.some((chg) => chg?.effectiveDate);

  if (
    !includes(EXCLUDE_END_DATE_CATEGORIES, subscriptionType) &&
    frequency?.recurringMode
  ) {
    if (includes(EXCLUDE_CONTRACT_RECURRING_MODE, frequency?.recurringMode)) {
      showChanges = false;
      if (isDisabled) {
        showChanges = true;
      }
    }
    if (frequency?.recurringMode === 'ARREAR') {
      if (!isEqual(initialServiceItem?.frequency, frequency)) {
        if (initialServiceItem?.frequency?.recurringMode !== 'ARREAR') {
          showChanges = true;
        }
        if (
          (initialServiceItem?.frequency?.quantity !== frequency?.quantity ||
            initialServiceItem?.frequency?.unit !== frequency?.unit) &&
          changeHasEffectiveDate
        ) {
          showChanges = false;
        }
      }
      if (
        !isEqual(initialServiceItem?.billDate, billDate) &&
        changeHasEffectiveDate
      ) {
        showChanges = false;
      }
    }
  } else if (
    initialServiceItem?.frequency?.type !== serviceItem?.frequency?.type
  ) {
    showChanges = false;
  }

  if (initialServiceItem?.unitType !== serviceItem?.unitType) {
    showChanges = false;
  }

  const quantityMap = {
    ASSET_VIEW: totalAssets,
    ALL_USER: totalUsers
  };

  const flags = useFlags();

  const updateItem = (updatedItem) => {
    handlePropertiesChange({
      ...updatedItem
    });
  };

  const [proratedAmount, setProratedAmount] = useState(0);
  const [overrideCost, setOverrideCost] = useState(unitPriceOverridden);
  const [initialUnitPrice, setInitialUnitPrice] = useState(unitPrice);

  const [initialRecurringMode, setInitialRecurringMode] = useState('UPFRONT');

  const [getProratedAmount, { loading, data, error }] = useLazyQuery(
    GET_PRORATED_AMOUNT,
    {
      client: AccountClient,
      onCompleted(response) {
        if (response) {
          setProratedAmount(response?.getProratedAmount);
        }
      },
      fetchPolicy: 'cache-and-network'
    }
  );
  // render only for custom unit type
  const renderQuantityGrid = () => {
    if (!showChanges) {
      let latestChange = change;
      if (
        !(
          initialRecurringMode === 'ARREAR' ||
          frequency?.recurringMode === 'ARREAR'
        ) &&
        includes(EXCLUDE_CONTRACT_RECURRING_MODE, frequency?.recurringMode)
      ) {
        latestChange = {
          ...pick(changes?.[0], ['changeId', 'quantity', 'effectiveDate']),
          ...change
        };
      }
      return (
        <QuantityCollector
          mode="DEFAULT"
          valueType="INTEGER"
          change={latestChange}
          handlePropChange={handlePropChange}
          effectiveDate={effectiveDate}
          billDate={billDate}
          nextBillDate={nextBillDate}
          endDate={endDate}
          intl={intl}
          currencyCode={currencyCode}
          countryCode={countryCode}
          subscriptionType={frequency?.unit}
          subscriptionQuantity={frequency?.quantity}
          frequency={frequency}
          unitPrice={unitPrice}
          getProratedAmount={getProratedAmount}
          proratedAmount={proratedAmount}
          unitType={unitType}
          loading={loading}
          totalAssets={totalAssets}
          totalUsers={totalUsers}
          quantityType={quantityType}
          quantity={quantity}
          clientId={clientId}
          formType={props.formType}
          errors={errors}
          integrationAddOn={integrationAddOn}
          subscriptionItemQuantityCount={subscriptionItemQuantityCount}
          isSiteSpecificEnabled={isSiteSpecificEnabled}
        />
      );
    }
    if (
      !includes(EXCLUDE_END_DATE_CATEGORIES, subscriptionType) &&
      changes?.length
    ) {
      if (change) {
        return (
          subscriptionType !== 'ONE-OFF' &&
          unitType === 'CUSTOM' && (
            <QuantityCollector
              mode="MODIFY"
              change={change}
              handlePropChange={handlePropChange}
              effectiveDate={effectiveDate}
              billDate={billDate}
              nextBillDate={nextBillDate}
              endDate={endDate}
              intl={intl}
              currencyCode={currencyCode}
              countryCode={countryCode}
              subscriptionType={frequency?.unit}
              frequency={frequency}
              unitPrice={unitPrice}
              getProratedAmount={getProratedAmount}
              proratedAmount={proratedAmount}
              loading={loading}
              unitType={unitType}
              totalAssets={totalAssets}
              totalUsers={totalUsers}
              quantityType={quantityType}
              quantity={quantity}
              clientId={clientId}
              formType={props.formType}
              errors={errors}
              integrationAddOn={integrationAddOn}
              subscriptionItemQuantityCount={subscriptionItemQuantityCount}
              isSiteSpecificEnabled={isSiteSpecificEnabled}
            />
          )
        );
      }
      if (!includes(['ONCE'], subscriptionType)) {
        return (
          subscriptionType !== 'ONE-OFF' &&
          unitType === 'CUSTOM' &&
          !props.pax8 && (
            <div className="mt-10">
              <Button
                plain
                onClick={() => {
                  handlePropChange('change', {
                    quantityChangeOperation: 'INCREASE'
                  });
                }}
              >
                <FormattedMessage id="client.contracts.modify.quantity" />
              </Button>
            </div>
          )
        );
      }
      return null;
    }
    return (
      <QuantityCollector
        mode="DEFAULT"
        valueType="INTEGER"
        change={change}
        handlePropChange={handlePropChange}
        effectiveDate={effectiveDate}
        billDate={billDate}
        nextBillDate={nextBillDate}
        endDate={endDate}
        intl={intl}
        currencyCode={currencyCode}
        countryCode={countryCode}
        subscriptionType={frequency?.unit}
        subscriptionQuantity={frequency?.quantity}
        frequency={frequency}
        unitPrice={unitPrice}
        getProratedAmount={getProratedAmount}
        proratedAmount={proratedAmount}
        unitType={unitType}
        loading={loading}
        totalAssets={totalAssets}
        totalUsers={totalUsers}
        quantityType={quantityType}
        quantity={quantity}
        clientId={clientId}
        formType={props.formType}
        errors={errors}
        integrationAddOn={integrationAddOn}
        subscriptionItemQuantityCount={subscriptionItemQuantityCount}
        isSiteSpecificEnabled={isSiteSpecificEnabled}
      />
    );
  };

  const handleChangeAndEmitDate = (val, change) => {
    const inputObj = {};
    if (change) {
      inputObj.change = {
        ...change,
        effectiveDate: null,
        quantity: change?.quantity || null
      };
    }
    handlePropertiesChange({
      billDate: val,
      endDate: null,
      ...inputObj
    });
  };

  function getTimeline(date) {
    if (dayjs(date).isBefore(dayjs())) {
      return 'past';
    }
    if (dayjs(date).isAfter(dayjs())) {
      return 'future';
    }
    return 'current';
  }

  useEffect(() => {
    if (frequency?.recurringMode) {
      setInitialRecurringMode(frequency?.recurringMode);
    }
  }, []);

  let nextBillDateBasedOnBillingType = nextBillDate;

  if (
    nextBillDateBasedOnBillingType &&
    frequency?.recurringMode === 'ADVANCE'
  ) {
    nextBillDateBasedOnBillingType = dayjs(nextBillDate).add(
      frequency?.recurringBefore,
      'days'
    );
  }
  if (nextBillDateBasedOnBillingType && frequency?.recurringMode === 'ARREAR') {
    nextBillDateBasedOnBillingType = dayjs(nextBillDate).subtract(
      frequency?.quantity,
      frequency?.unit?.toLowerCase()
    );
  }

  return (
    <>
      <div className="flex g20 width-600">
        <Field>
          {configurableSections.indexOf('costPrice') > -1 && (
            <>
              <Label>
                <FormattedMessage id="admin.serviceCatalog.offeringType.quantity.cost" />
              </Label>
              <div className="flex actr mb-10">
                <TextField
                  size="extrasmall"
                  value={costPrice || ''}
                  min={0}
                  type="number"
                  onChange={(val) => {
                    handlePropChange('costPrice', val);
                  }}
                  disabled={
                    isDisabled ||
                    (disabledSections &&
                      includes(disabledSections, 'costPrice'))
                  }
                  suffix={currencySymbolWithCode}
                />
              </div>
            </>
          )}
          <Label>
            <FormattedMessage id="admin.serviceCatalog.unitPrice" />
          </Label>
          <div className="flex actr mb-10">
            <TextField
              size="default"
              value={unitPrice}
              min={0}
              type="number"
              onChange={(val) => {
                handlePropChange('unitPrice', val);
              }}
              onBlur={() => {
                if (
                  (unitType && unitType !== 'CUSTOM') ||
                  (change?.effectiveDate &&
                    change?.quantityChangeOperation === 'INCREASE')
                ) {
                  getProratedAmount({
                    variables: {
                      itemInput: {
                        quantityType: 'UNITS',
                        billDate: billDate,
                        effectiveDate: change?.effectiveDate,
                        unitPrice: unitPrice,
                        quantity: change?.quantity
                          ? change?.quantity
                          : undefined,
                        // subscriptionType: subscriptionType,
                        frequency: frequency,
                        endDate: endDate || undefined,
                        clientId: clientId
                      }
                    },
                    input: {
                      itemInput: {
                        quantityType: 'UNITS',
                        billDate: billDate,
                        effectiveDate: change?.effectiveDate,
                        unitPrice: unitPrice,
                        quantity: change?.quantity
                          ? change?.quantity
                          : undefined,
                        // subscriptionType: subscriptionType,
                        frequency: frequency,
                        endDate: endDate || undefined,
                        clientId: clientId
                      }
                    }
                  });
                }
              }}
              disabled={
                (isDisabled && !overrideCost) ||
                (disabledSections && includes(disabledSections, 'unitPrice'))
              }
              suffix={currencySymbolWithCode}
            />
          </div>
        </Field>
        {configurableSections.indexOf('quantity') > -1 ? (
          <div className="mt25 w400">
            <Label>
              <FormattedMessage id="admin.serviceCatalog.quantity" />
            </Label>
            <div className="flex full-width center g10">
              <ItemQuantity
                item={serviceItem}
                handlePropertiesChange={handlePropertiesChange}
                isDisabled={isDisabled}
                isIntegrationAddOnItem={isIntegrationAddOnItem}
                integrationAddOn={integrationAddOn}
                entryPoint={entryPoint}
              />
              {integrationAddOn ? (
                <div className="mr-10">
                  <SuperPop
                    position="top-start"
                    content={getTranslatedText(
                      intl,
                      'client.contract.serviceItem.integration.superTip.content'
                    )}
                  />
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        ) : (
          ''
        )}
        {serviceItem?.unitType === 'ASSET_VIEW' && (
          <Field>
            <ItemQuantitySubType
              label={
                <Label>
                  <FormattedMessage id="admin.serviceCatalog.condition" />
                </Label>
              }
              onChange={(val) => {
                // eslint-disable-next-line no-unused-expressions
                serviceItemSheetCtx?.setSelectedAssetView(val);
              }}
              selectedValue={serviceItem?.assetView}
              isDisabled={isDisabled}
            />
          </Field>
        )}
        {props.pax8 && formType !== 'service-catalog' ? (
          <Field>
            <div className="flex center">
              <Checkbox
                label={getTranslatedText(
                  intl,
                  'client.contract.serviceItem.overrideCost'
                )}
                checked={overrideCost}
                onChange={(val) => {
                  setOverrideCost(val);
                  if (!val) {
                    handlePropertiesChange({
                      unitPrice: initialUnitPrice,
                      unitPriceOverridden: val
                    });
                  } else {
                    handlePropertiesChange({
                      unitPriceOverridden: val
                    });
                  }
                }}
              />
              <div className="ml-20">
                <SuperPop
                  position="top-start"
                  content={getTranslatedText(
                    intl,
                    'client.contract.serviceItem.overrideCost.supertip'
                  )}
                />
              </div>
            </div>
          </Field>
        ) : (
          ''
        )}
      </div>
      <div className="flex g20 width-600 col">
        {configurableSections.indexOf('subscriptionType') > -1 ? (
          <SubscriptionSelectField
            intl={intl}
            clearChanges={changes?.some((chg) => chg?.effectiveDate)}
            serviceItem={serviceItem}
            isDisabled={integrationAddOn || isDisabled}
            handlePropChange={handlePropChange}
            handlePropertiesChange={handlePropertiesChange}
            subscriptionTypes={subscriptionTypes}
            customSubscriptionTypes={customSubscriptionTypes}
          />
        ) : (
          ''
        )}
        <div className="fx actr">
          {configurableSections.indexOf('effectiveDate') > -1 ? (
            <Field>
              <div>
                <Label>
                  <FormattedMessage id="client.details.contract.effectiveDate" />
                </Label>

                <DatePicker
                  selected={billDate && dayjs(billDate).toDate()}
                  onChange={(date) => {
                    const formattedDate = date
                      ? dayjs(date).format('YYYY-MM-DD')
                      : null;
                    handleChangeAndEmitDate(formattedDate, change);
                  }}
                  placeholderText="YYYY-MM-DD"
                  dateFormat="yyyy-MM-dd"
                  title={getTranslatedText(
                    intl,
                    'client.details.contract.effectiveDate'
                  )}
                  required
                  disabled={integrationAddOn || isDisabled}
                />
              </div>
            </Field>
          ) : (
            ''
          )}
          {includes(configurableSections, 'endDate') && // Should not show end date if subscription type is one of EXCLUDE_END_DATE_CATEGORIES
          subscriptionType &&
          !includes(EXCLUDE_END_DATE_CATEGORIES, subscriptionType) ? (
            <Field>
              <EndDate
                intl={intl}
                endDate={endDate}
                minDate={
                  dayjs(billDate).isAfter(dayjs()) ? billDate : new Date()
                }
                // minDate={
                //   !isEmpty(frequency) &&
                //   getMinEndDate({
                //     billDate: billDate,
                //     change: change,
                //     changes: changes,
                //     nextBillDate: nextBillDate,
                //     frequency: frequency,
                //     endDate: endDate,
                //     canEdit: !isDisabled
                //   })
                // }
                enableReminder={enableReminder}
                remindDuration={remindDuration}
                handlePropChange={handlePropChange}
                handlePropertiesChange={handlePropertiesChange}
                isDisabled={integrationAddOn || isEditEndDatedisabled}
              />
            </Field>
          ) : (
            ''
          )}
        </div>
      </div>

      <Field>
        <div className="flex center">
          <Checkbox
            label={getTranslatedText(
              intl,
              'admin.serviceCatalog.useAsWorklogItem'
            )}
            checked={useAsWorklogItem}
            onChange={(val) => {
              handlePropChange('useAsWorklogItem', val);
            }}
            disabled={
              isIntegrationAddOnItem ||
              isDisabled ||
              (disabledSections && includes(disabledSections, 'useAsWorklog'))
            }
          />
          <div className="ml8">
            <SuperPop
              position="top-start"
              content={getTranslatedText(
                intl,
                'admin.serviceCatalog.useAsWorklogItem.superTip.content'
              )}
            />
          </div>
        </div>
      </Field>

      <div className="mt-20">
        {frequency?.type === 'CUSTOM' &&
          nextBillDateBasedOnBillingType &&
          ((endDate &&
            dayjs(nextBillDateBasedOnBillingType).isSameOrBefore(endDate)) ||
            endDate === null) && (
            <Badge status="attention">
              <span className="fx actr g10 p5">
                <SVGIcon name="About" />
                <FormattedMessage
                  id="client.details.contractinvoice.next.billdate"
                  values={{
                    nextBillDate: nextBillDateLoading ? (
                      <Spinner size="small" />
                    ) : (
                      nextBillDate
                    )
                  }}
                />
              </span>
            </Badge>
          )}
      </div>

      {true && formType !== 'service-catalog' && (
        <div className="mt-40">
          <Heading element="h2">
            <FormattedMessage id="contract.serviceItem.quantity.heading" />
          </Heading>
          {!includes(EXCLUDE_END_DATE_CATEGORIES, subscriptionType) &&
          showChanges &&
          changes ? (
            <>
              {changes.map((c, idx) => {
                return (
                  <div className="flex actr g20 mt-10">
                    <Field>
                      <Label>
                        <FormattedMessage
                          id={
                            idx === 0
                              ? 'client.contracts.queue.quantity'
                              : 'client.contracts.queue.changeInquantity'
                          }
                        />
                      </Label>

                      <TextField
                        value={
                          unitType !== 'CUSTOM'
                            ? isSiteSpecificEnabled || unitType !== 'ALL_USER'
                              ? subscriptionItemQuantityCount
                              : String(quantityMap[unitType])
                            : String(c.quantity)
                        }
                        type="number"
                        disabled
                        suffix={
                          c.quantityChangeOperation ? (
                            <div className="ptr bshadow flex rounder-4px h-25 cursor-not-allowed">
                              {c.quantityChangeOperation === 'INCREASE' && (
                                <Operation
                                  selected={
                                    c.quantityChangeOperation === 'INCREASE'
                                  }
                                >
                                  <SVGIcon
                                    name="ArrowDown"
                                    color={
                                      c.quantityChangeOperation === 'INCREASE'
                                        ? 'white'
                                        : 'black'
                                    }
                                  />
                                </Operation>
                              )}
                              {c.quantityChangeOperation === 'DECREASE' && (
                                <Operation
                                  type="DECREASE"
                                  selected={
                                    c.quantityChangeOperation === 'DECREASE'
                                  }
                                >
                                  <SVGIcon
                                    name="ArrowDown"
                                    color={
                                      c.quantityChangeOperation === 'DECREASE'
                                        ? 'white'
                                        : 'black'
                                    }
                                  />
                                </Operation>
                              )}
                            </div>
                          ) : (
                            ''
                          )
                        }
                      />
                    </Field>
                    {c.effectiveDate && (
                      <Field>
                        <div>
                          <Label>
                            <FormattedMessage id="client.contracts.item.effectiveDate" />
                          </Label>

                          <DatePicker
                            selected={
                              c.effectiveDate && dayjs(c.effectiveDate).toDate()
                            }
                            placeholderText="YYYY-MM-DD"
                            dateFormat="yyyy-MM-dd"
                            title={getTranslatedText(
                              intl,
                              'client.contracts.item.effectiveDate'
                            )}
                            required
                            disabled
                          />
                        </div>
                      </Field>
                    )}
                    {c.proRatedAmount && (
                      <ProrateWrap className="flex">
                        <div>
                          <Label>
                            <FormattedMessage id="client.contracts.item.proratedAmount" />
                          </Label>

                          <TextField
                            size="extrasmall"
                            value={c.proRatedAmount}
                            min={0}
                            type="number"
                            disabled
                            suffix={currencySymbolWithCode}
                          />
                        </div>
                        <div className="ml-20">
                          <Checkbox
                            label={getTranslatedText(
                              intl,
                              'client.contracts.item.generateInvoice'
                            )}
                            checked={c.generateInvoice}
                            disabled
                          />
                        </div>
                      </ProrateWrap>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            ''
          )}
          {formType !== 'service-catalog' && renderQuantityGrid()}
        </div>
      )}
    </>
  );
}

export default injectIntl(UnitTypeSection);
